import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { Controller, useForm } from 'react-hook-form'
import { Button, Modal, TextField, Typography } from '@mui/material'
import { makeStyles, useTheme } from '@mui/styles'
import Cookie from 'js-cookie'

import { Card, Form } from 'ui'
import { AskResetPassword } from 'components/auth/AskResetPassword'

import { useRouter } from 'hooks/router'
import { clientNameAtom, logoAtom } from 'recoil/atoms'
import { reqKy } from 'stores/api'
import { useUserStore } from 'stores/user'
import { useGlobalSnackbarStore } from 'stores/globalSnackbar'
import { api, cookies, urls } from 'utils/constants'

const useStyles = makeStyles((theme) => ({
    root: {
        width: '300px',
        [theme.breakpoints.up('sm')]: {
            width: '500px',
        },
    },

    logo: {
        width: '80%',
    },

    title: {
        margin: '2vh 0',
    },

    textField: {
        margin: '1vh 0',
    },

    login: {
        marginTop: '5vh',
        color: theme.palette.secondary.main,
    },

    register: {
        marginTop: '5vh',
    },

    modal: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },

    forgottenPassword: {
        marginTop: '2vh',
        '&:hover': {
            backgroundColor: 'unset',
            textDecoration: 'underline',
        },
    },
}))

const Login = ({ setTheme }) => {
    const theme = useTheme()
    const classes = useStyles(theme)
    const router = useRouter()
    const { t } = useTranslation()
    const { clientName } = useParams()
    const { initialiseUser } = useUserStore()
    const { showGlobalSnackbar } = useGlobalSnackbarStore()

    const logo = useRecoilValue(logoAtom)
    const setClientName = useSetRecoilState(clientNameAtom)

    const [wantsResetPassword, setWantsResetPassword] = useState(false)

    const { control, handleSubmit } = useForm({
        defaultValues: {
            email: '',
            password: '',
        },
    })

    /** Close the forgotten password dialog. */
    const handleCloseForgottenPassword = () => {
        setWantsResetPassword(false)
    }

    const login = async (data) => {
        if (!data.email || !data.password) {
            return showGlobalSnackbar('error', t('errorTryLater'))
        }

        const requestLogin = await reqKy
            .post(api.auth.LOGIN, {
                json: {
                    email: data.email,
                    password: data.password,
                },
            })
            .json()
            .catch(async (error) => {
                const errorBody = await error.response.json()

                if (errorBody.non_field_errors) {
                    return showGlobalSnackbar(
                        'error',
                        t('emailOrPasswordIncorrect'),
                    )
                }

                showGlobalSnackbar('error', t('errorTryLater'))
            })

        if (!requestLogin) return

        Cookie.set(cookies.ACCESS_TOKEN, requestLogin.access, cookies.OPTIONS)
        Cookie.set(cookies.REFRESH_TOKEN, requestLogin.refresh, cookies.OPTIONS)
        Cookie.set(cookies.ID, requestLogin.machine_user_id, cookies.OPTIONS)

        await initialiseUser(setTheme)

        setClientName(requestLogin.client.name)

        router.push(`/${requestLogin.client.name}${urls.HOMEPAGE}`)
    }

    // Update the manager name
    useEffect(() => {
        setClientName(clientName)
    }, [clientName, setClientName])

    return (
        <Card className={classes.root}>
            <Form onSubmit={handleSubmit(login)}>
                {/* Logo */}
                <img
                    className={classes.logo}
                    src={logo}
                    alt={clientName}
                />

                {/* Title */}
                <Typography
                    className={classes.title}
                    variant='h1'
                >
                    {t('login')}
                </Typography>

                {/* Email */}
                <Controller
                    name='email'
                    control={control}
                    render={({ field }) => (
                        <TextField
                            className={classes.textField}
                            label={t('email')}
                            {...field}
                        />
                    )}
                />

                {/* Password */}
                <Controller
                    name='password'
                    control={control}
                    render={({ field }) => (
                        <TextField
                            className={classes.textField}
                            label={t('password')}
                            type='password'
                            {...field}
                        />
                    )}
                />

                {/* Login button */}
                <Button
                    className={classes.login}
                    variant='contained'
                    size='large'
                    type='submit'
                    fullWidth
                >
                    {t('login')}
                </Button>

                {/* Create an account */}
                {!!clientName && (
                    <Button
                        className={classes.register}
                        variant='outlined'
                        size='small'
                        onClick={() => router.push(urls.auth.REGISTER)}
                    >
                        {t('createAccount')}
                    </Button>
                )}
            </Form>

            {/* Ask for a reset password email */}
            <Button
                className={classes.forgottenPassword}
                size='small'
                onClick={() => {
                    setWantsResetPassword(true)
                }}
            >
                {t('forgottenPassword')}
            </Button>

            {/* Forgotten password dialog */}
            <Modal
                className={classes.modal}
                open={wantsResetPassword}
                onClose={handleCloseForgottenPassword}
            >
                <AskResetPassword closeDialog={handleCloseForgottenPassword} />
            </Modal>
        </Card>
    )
}

export default Login
